import { format, startOfWeek, endOfWeek, eachWeekOfInterval, parseISO, addDays, startOfMonth, isBefore, isAfter } from 'date-fns';
import { EChartsOption } from "echarts";
import {
  applyColors,
  coreServicesLabels,
  getStyleProp,
  InverterV2,
  NodeType,
  parseSKonAggregateDailies,
  Range,
  telemetryStyles,
} from "ngx-core-services";

import { getStackedEnergySeries, sumDailyEnergies } from "../utils";
import { InverterEnergySeries, PickerMode } from "./monthly-yearly-energy-overview.model";
import { EnergyStatisticsInput } from "../components/energy-statistics-card/energy-statistics.model";
import { ENERGY_UNIT, IRRADIATION_ENERGY_UNIT, IRRADIATION_SERIES_NAME, SECONDARY_UNIT_SERIES } from "./monthly-yearly-energy-overview.constants";

// Helper functions for axis management
const isSecondaryUnit = (name: string) => SECONDARY_UNIT_SERIES.includes(name as typeof SECONDARY_UNIT_SERIES[number]);

const getAxisConfig = (selected: { [key: string]: boolean }) => {
  const hasEnergy = Object.entries(selected).some(([name, isSelected]) => isSelected && !isSecondaryUnit(name));
  const hasSecondary = Object.entries(selected).some(([name, isSelected]) => isSelected && isSecondaryUnit(name));
  return { hasEnergy, hasSecondary };
};

const baseAxisStyle = {
  type: 'value' as const,
  nameLocation: 'end' as const,
  nameTextStyle: { color: getStyleProp('--grey-500'), fontWeight: 'bold' as const },
  splitLine: { show: false },
  axisLabel: {
    showMinLabel: false,
    showMaxLabel: false,
    color: getStyleProp('--grey-500'),
    fontWeight: 'bold' as const,
    formatter: (value: any) => `${Math.abs(value)}`,
  },
};

const buildYAxis = (hasEnergy: boolean, hasSecondary: boolean, hasNegativeEnergy: boolean): any[] => {
  const yAxis: any[] = [];
  if (hasEnergy) {
    yAxis.push({
      ...baseAxisStyle,
      name: ENERGY_UNIT,
      min: hasNegativeEnergy ? (value: any) => Math.min(0, value.min) : 0,
      max: (value: any) => Math.max(0, value.max),
    });
  }
  if (hasSecondary) {
    // Secondary goes RIGHT only if energy is present, otherwise LEFT
    const position = hasEnergy ? 'right' : 'left';
    yAxis.push({
      ...baseAxisStyle,
      name: IRRADIATION_ENERGY_UNIT,
      position,
      min: 0,
      max: (value: any) => Math.max(0, value.max),
    });
  }
  return yAxis;
};

const getYAxisIndex = (seriesName: string, hasEnergy: boolean, hasSecondary: boolean): number => {
  if (isSecondaryUnit(seriesName) && hasSecondary) {
    return hasEnergy ? 1 : 0;
  }
  return 0;
};

const Months = [
  coreServicesLabels.Jan,
  coreServicesLabels.Feb,
  coreServicesLabels.Mar,
  coreServicesLabels.Apr,
  coreServicesLabels.May,
  coreServicesLabels.Jun,
  coreServicesLabels.Jul,
  coreServicesLabels.Aug,
  coreServicesLabels.Sep,
  coreServicesLabels.Oct,
  coreServicesLabels.Nov,
  coreServicesLabels.Dec,
];

export const buildEnergyGraph = (
  series: InverterEnergySeries,
  dates: string[],
  selected: { [key: string]: boolean },
): { month: EChartsOption; year: EChartsOption; weeks: EChartsOption[]; halfYear: EChartsOption[] } => {
  /**
   * Keep EG3 (dailyImportedEnergy) and EG6 (dailyDischargedEnergy) below zero.
   * This is intentional to mirror the direction convention used in the power
   * chart, so monthly/yearly energy bars stay visually aligned with power daily graph.
   */
  const belowZero = ['EG3', 'EG6'];

  const processData = (series: InverterEnergySeries) => {
    return Object.entries(series)
      .filter(([k, v]) => v?.values?.length)
      .map(([k, v]) => ({
        ...v,
        values: v.values.map((value) => {
          if (belowZero.includes(k)) {
            return Number(value) * -1 || 0;
          }
          return Number(value) || 0;
        }),
      }));
  };

  const generateGraphOptions = (data: any, dates: string[], mode: 'month' | 'year' | 'weeks' | 'halfYear'): EChartsOption => {
    const { hasEnergy, hasSecondary } = getAxisConfig(selected);
    const hasNegativeEnergy = data
      .filter((s: any) => !isSecondaryUnit(s.name))
      .some((s: any) => s.values?.some((v: number) => v < 0));

    return {
      grid: {
        left: 5,
        right: 5,
        bottom: 5,
        containLabel: true
      },
      legend: {
        show: false,
        selected,
      },
      tooltip: {
        confine: true,
        trigger: 'axis',
        formatter: (elements: any): string => {
          const { dataIndex } = elements[0];
          const date = dates[dataIndex];
          const dateLabel = new Date(date);

          const headerTemplate = elements.length > 0 ? `
            <div class="fx-row text-accent-700 fx-align-center fx-justify-between tw-text-sm p-1 bg-accent-100 radius-t-4">
              <div class="tw-font-sm ml-auto">${(mode === 'year' || mode === 'halfYear') ? Months[dateLabel.getMonth()] : date}</div>
            </div>
          ` : '';

          const detailsTemplate = elements.map((element: any) => {
            const {
              seriesName,
              data: value,
            } = element;
            const { name, color: _color } = telemetryStyles(seriesName);
            const bgColor = `bg-${_color?.split('text-')?.[1] || ''}`;
            const unit = seriesName === IRRADIATION_SERIES_NAME ? IRRADIATION_ENERGY_UNIT : ENERGY_UNIT;
            if (value !== 0) {
              return `
                <div class="fx-row fx-align-center fx-justify-between tw-text-sm text-grey-400 p-1">
                  <div class="tw-font-medium text-style-italic mr-5 fx-row">
                    <div class="s-4 rounded mr-3 ${bgColor}"></div>
                    ${name}
                  </div>
                  <div class="tw-font-normal">${Math.abs(value).toFixed(2)} <span class="tw-text-xs tw-font-bold">${unit}</span></div>
                </div>`;
            } else {
              return '';
            }
          }).join('');

          return `${headerTemplate}${detailsTemplate}`;
        },
        backgroundColor: getStyleProp('--white-100'),
        borderColor: getStyleProp('--grey-300'),
        shadowBlur: 0,
        shadowOffsetX: 0,
        shadowOffsetY: 0,
        borderRadius: 4,
        padding: 0,
        textStyle: {
          color: getStyleProp('--white'),
        },
      },
      xAxis: {
        data: dates.map((date) => {
          const dateLabel = new Date(date);
          if (mode === 'year' || mode === 'halfYear') {
            return Months[dateLabel.getMonth()];
          }
          return `${dateLabel.getDate()} ${Months[dateLabel.getMonth()]}`;
        }),
        type: 'category',
        axisLine: {
          onZero: true,
          show: false,
        },
        axisTick: {
          show: false,
        },
        splitLine: {
          show: false,
        },
        axisLabel: {
          color: getStyleProp('--grey-300'),
          fontWeight: 'bold',
          showMinLabel: true,
          showMaxLabel: true,
          hideOverlap: true,
          rotate: 45,
        },
      },
      yAxis: buildYAxis(hasEnergy, hasSecondary, hasNegativeEnergy),
      series: data.map((s: any) => ({
        name: s.name,
        type: 'bar',
        color: s.color,
        data: s.values,
        stack: getStackedEnergySeries(s.name),
        yAxisIndex: getYAxisIndex(s.name, hasEnergy, hasSecondary),
        emphasis: {
          focus: 'series',
          itemStyle: {
            shadowBlur: 10,
            shadowColor: 'rgba(0,0,0,0.3)'
          }
        }
      })),
    };
  };

  const splitIntoWeeks = (dates: string[]): string[][] => {
    const parsedDates = dates.map(date => parseISO(date));
    const weeksIntervals = eachWeekOfInterval({
      start: startOfWeek(parsedDates[0], { weekStartsOn: 1 }),
      end: endOfWeek(parsedDates[parsedDates.length - 1], { weekStartsOn: 1 })
    }, { weekStartsOn: 1 });

    return weeksIntervals.map((startOfWeekDate) => {
      const endOfWeekDate = endOfWeek(startOfWeekDate, { weekStartsOn: 1 });
      const weekDates = [];

      for (let date = startOfWeekDate; date <= endOfWeekDate; date = addDays(date, 1)) {
        const dateStr = format(date, 'yyyy-MM-dd');
        weekDates.push(dateStr);
      }

      return weekDates;
    });
  };

  const splitIntoHalfYear = (dates: string[]): string[][] => {
    const parsedDates = dates.map(date => parseISO(date));
    const firstHalfEndDate = addDays(startOfMonth(parsedDates[0]), 182);

    const firstHalf = dates.filter(date => isBefore(parseISO(date), firstHalfEndDate));
    const secondHalf = dates.filter(date => isAfter(parseISO(date), firstHalfEndDate));

    return [firstHalf, secondHalf];
  };

  const data = processData(series);

  const monthGraph = generateGraphOptions(data, dates, 'month');
  const yearGraph = generateGraphOptions(data, dates, 'year');

  const weeksDates = splitIntoWeeks(dates);
  const weeksGraphs = weeksDates.map((weekDates) => {
    const dateData = data.map((s: InverterEnergySeries[keyof InverterV2]) => ({
      ...s,
      values: weekDates.map((date) => {
        const index = dates.indexOf(date);
        return s.values[index] || 0;
      }),
    }))

    const isEmpty = dateData.every((s) => s.values.every((v) => v === 0));
    if (isEmpty) return;

    return generateGraphOptions(dateData, weekDates, 'weeks')
  }).filter((g): g is EChartsOption => !!g);

  const halfYearDates = splitIntoHalfYear(dates);
  const halfYearGraphs = halfYearDates.map((halfYearDates) => {
    const dateData = data.map((s: InverterEnergySeries[keyof InverterV2]) => ({
      ...s,
      values: halfYearDates.map((date) => {
        const index = dates.indexOf(date);
        return s.values[index] || 0;
      }),
    }));

    const isEmpty = dateData.every((s) => s.values.every((v) => v === 0));
    if (isEmpty) return;

    return generateGraphOptions(dateData, halfYearDates, 'halfYear');
  }).filter((g): g is EChartsOption => !!g);

  return {
    month: monthGraph,
    year: yearGraph,
    weeks: weeksGraphs,
    halfYear: halfYearGraphs,
  };
};

export const getAggregateEnergySeries = (data: Partial<InverterV2>[], range: Range, mode: PickerMode): {
  energyAggregates: InverterEnergySeries;
  periodDates: string[];
} => {
  const to = new Date(range.to);
  const from = new Date(range.from);

  const periodDates: string[] = generatePeriodDates(from, to, mode);
  const energies: Partial<InverterV2>[] = generateEnergies(data, periodDates, mode);

  const energyAggregates = createEnergyAggregates(energies);
  return {
    energyAggregates,
    periodDates,
  };
};

const generatePeriodDates = (from: Date, to: Date, mode: PickerMode): string[] => {
  const dates: string[] = [];
  to.setHours(23, 59, 59, 999);
  for (let d = new Date(from); d <= to; increaseStep(d, mode)) {
    dates.push(format(d, 'yyyy-MM-dd'));
  }
  return dates;
};

const increaseStep = (d: Date, mode: PickerMode): void => {
    if (mode === 'year') {
    d.setMonth(d.getMonth() + 1);
  } else {
    d.setDate(d.getDate() + 1);
  }
};

const getSignificativeDate = (d: Date, mode: PickerMode): number => {
  return mode === 'year' ? d.getMonth() : d.getDate();
};

const generateEnergies = (data: Partial<InverterV2>[], periodDates: string[], mode: PickerMode): Partial<InverterV2>[] => {
  return periodDates.map(date => {
    const d = new Date(date);
    return sumDailyEnergies(data.filter(e => {
      const currentDate = parseSKonAggregateDailies(e.SK);
      return currentDate && getSignificativeDate(new Date(currentDate), mode) === getSignificativeDate(d, mode);
    }));
  });
};

const createEnergyAggregates = (energies: Partial<InverterV2>[]): InverterEnergySeries => {
  const energyNames = ['EG1', 'EG2', 'EG3', 'EG4', 'EG5', 'EG6', 'SOL_EG'] as Array<keyof InverterV2>;

  const energyValues = energyNames.map((name) => ({
    values: energies.map((e) => e[name] || null),
    name,
  })).reduce((prev, curr) => ({
    ...prev,
    [curr.name]: {
      values: curr.values,
      name: curr.name,
    },
  }), {} as InverterEnergySeries);

  return applyColors(energyValues);
};

export const updateSelectedAndAxisLimits = (option: EChartsOption, selected: { [key: string]: boolean }): EChartsOption => {
  const { hasEnergy, hasSecondary } = getAxisConfig(selected);
  const series = (option.series as any[]) || [];
  const hasNegativeEnergy = series
    .filter((s) => !isSecondaryUnit(s.name))
    .some((s) => (s.data || []).some((v: number) => v < 0));

  const updatedSeries = series.map((s) => ({
    ...s,
    yAxisIndex: getYAxisIndex(s.name, hasEnergy, hasSecondary),
  }));

  return {
    ...option,
    legend: { ...option.legend, selected },
    yAxis: buildYAxis(hasEnergy, hasSecondary, hasNegativeEnergy),
    series: updatedSeries,
  };
};

export const getEnergiesStatistics = (energyAggregates: InverterEnergySeries, energyToShow:  NodeType[]): EnergyStatisticsInput => {
  const productionEnergies = getEnergyValues('EG1', energyAggregates);
  const consumptionEnergies = getEnergyValues('EG4', energyAggregates);
  const importEnergies = getEnergyValues('EG3', energyAggregates);
  const exportEnergies = getEnergyValues('EG2', energyAggregates);
  const dischargeEnergies = getEnergyValues('EG6', energyAggregates);
  const chargeEnergies = getEnergyValues('EG5', energyAggregates);
  const irradiationEnergies = getEnergyValues('SOL_EG', energyAggregates);

  return {
    generated: energyToShow.includes('solar') ? getEnergyStatistic(productionEnergies) : undefined,
    consumed: energyToShow.includes('home') ? getEnergyStatistic(consumptionEnergies) : undefined,
    grid: energyToShow.includes('grid') ? {
      import: getEnergyStatistic(importEnergies),
      export: getEnergyStatistic(exportEnergies),
    } : undefined,
    battery: energyToShow.includes('battery') ? {
      charged: getEnergyStatistic(chargeEnergies),
      discharged: getEnergyStatistic(dischargeEnergies),
    } : undefined,
    irradiation: energyToShow.includes('irradiance') ? getEnergyStatistic(irradiationEnergies) : undefined,
  };
}

export const getEnergyStatistic = (energy: number[]): {
  min: number;
  max: number;
  avg: number;
  total: number;
} => {
  return {
    min: energy.length ? Math.min(...energy) || 0 : 0,
    max: energy.length ? Math.max(...energy) || 0 : 0,
    avg: energy.reduce((a, b) => a + b, 0) / energy.length || 0,
    total: energy.reduce((a, b) => a + b, 0) || 0,
  };
}

export const getEnergyValues = (key: keyof InverterEnergySeries, energyAggregates: InverterEnergySeries): number[] => energyAggregates[key].values.map((v) => v ? +v : 0)
